import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ReactFlow, ReactFlowProvider, Controls, Background, Handle, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Lightbulb, Calendar, Heart, Search, Download, X, FileText } from 'lucide-react';
import { toPng } from 'html-to-image';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../hooks/useFirebase';
import { APP_ID } from '../constants/config';

const formatReadableDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      const year = parts[0];
      const monthNum = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const malayMonths = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
        'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
      return `${day} ${malayMonths[monthNum]} ${year}`;
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' });
    }
  } catch (e) { /* ignore */ }
  return dateStr;
};

// Custom read-only node
const ReadOnlyNode = ({ data }) => {
  const isRoot = data.isRoot;
  const isHighlighted = data.isHighlighted;
  const isDimmed = data.isDimmed;

  return (
    <div
      className={`shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 ${
        isHighlighted ? 'ring-4 ring-amber-500 shadow-md scale-[1.05]' : ''
      }`}
      style={{
        backgroundColor: isRoot ? '#292524' : (data.color || '#f5f5f4'),
        borderRadius: '8px',
        minWidth: isRoot ? '120px' : '100px',
        maxWidth: '200px',
        padding: isRoot ? '12px 20px' : '8px 16px',
        border: isRoot ? 'none' : (data.color ? 'none' : '1px solid #e7e5e4'),
        opacity: isDimmed ? 0.35 : 1,
      }}
    >
      <div
        className="font-semibold leading-snug break-words"
        style={{
          fontSize: isRoot ? '14px' : '12px',
          color: (isRoot || data.color) ? '#fff' : '#292524',
          textAlign: 'center',
        }}
      >
        {data.label || 'Untitled'}
      </div>
      {data.description && (
        <div
          className="mt-1 leading-relaxed"
          style={{
            fontSize: '10px',
            color: (isRoot || data.color) ? 'rgba(255,255,255,0.65)' : '#a8a29e',
            textAlign: 'center',
          }}
        >
          {data.description}
        </div>
      )}
      {/* Hidden handles needed for edge rendering */}
      <Handle id="target-top" type="target" position={Position.Top} style={{ opacity: 0, width: 0, height: 0 }} />
      <Handle id="target-left" type="target" position={Position.Left} style={{ opacity: 0, width: 0, height: 0 }} />
      <Handle id="target-right" type="target" position={Position.Right} style={{ opacity: 0, width: 0, height: 0 }} />
      <Handle id="target-bottom" type="target" position={Position.Bottom} style={{ opacity: 0, width: 0, height: 0 }} />

      <Handle id="source-top" type="source" position={Position.Top} style={{ opacity: 0, width: 0, height: 0 }} />
      <Handle id="source-left" type="source" position={Position.Left} style={{ opacity: 0, width: 0, height: 0 }} />
      <Handle id="source-right" type="source" position={Position.Right} style={{ opacity: 0, width: 0, height: 0 }} />
      <Handle id="source-bottom" type="source" position={Position.Bottom} style={{ opacity: 0, width: 0, height: 0 }} />
    </div>
  );
};

const nodeTypes = { mindMap: ReadOnlyNode };

const getEdgeHandles = (sourceNode, targetNode) => {
  if (!sourceNode || !targetNode) return { sourceHandle: 'source-bottom', targetHandle: 'target-top' };
  const dx = targetNode.position.x - sourceNode.position.x;
  const dy = targetNode.position.y - sourceNode.position.y;
  
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0 
      ? { sourceHandle: 'source-right', targetHandle: 'target-left' } 
      : { sourceHandle: 'source-left', targetHandle: 'target-right' };
  } else {
    return dy > 0 
      ? { sourceHandle: 'source-bottom', targetHandle: 'target-top' } 
      : { sourceHandle: 'source-top', targetHandle: 'target-bottom' };
  }
};

const BankIdeaView = ({ idea, handleNavigate, isDarkMode, theme }) => {
  const rfInstance = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNodeDetails, setSelectedNodeDetails] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(idea?.likes || 0);

  useEffect(() => {
    try {
      const liked = JSON.parse(localStorage.getItem('likedIdeas') || '[]');
      setIsLiked(liked.includes(idea.id));
    } catch (e) {}
  }, [idea.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (rfInstance.current) {
        rfInstance.current.fitView({ padding: 0.1 });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleLike = async () => {
    const newLiked = !isLiked;
    setIsLiked(newLiked);
    setLikesCount(prev => Math.max(0, prev + (newLiked ? 1 : -1)));
    
    try {
      const liked = JSON.parse(localStorage.getItem('likedIdeas') || '[]');
      const updatedLiked = newLiked ? [...liked, idea.id] : liked.filter(id => id !== idea.id);
      localStorage.setItem('likedIdeas', JSON.stringify(updatedLiked));
      
      const ideaRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'ideas', idea.id);
      await updateDoc(ideaRef, {
        likes: increment(newLiked ? 1 : -1)
      });
    } catch (error) {
      console.error("Failed to like idea:", error);
    }
  };

  const handleExport = async () => {
    const element = document.querySelector('.react-flow');
    if (!element) return;
    
    const background = element.querySelector('.react-flow__background');
    const controls = element.querySelector('.react-flow__controls');
    const attribution = element.querySelector('.react-flow__attribution');
    
    if (background) background.style.display = 'none';
    if (controls) controls.style.display = 'none';
    if (attribution) attribution.style.display = 'none';
    
    try {
      const dataUrl = await toPng(element, {
        backgroundColor: '#ffffff',
        style: {
          transform: 'none',
        }
      });
      
      if (background) background.style.display = '';
      if (controls) controls.style.display = '';
      if (attribution) attribution.style.display = '';
      
      const link = document.createElement('a');
      link.download = `${idea.title || 'mind-map'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      if (background) background.style.display = '';
      if (controls) controls.style.display = '';
      if (attribution) attribution.style.display = '';
      console.error('Export failed:', error);
    }
  };

  const handleExportPDF = async () => {
    const element = document.querySelector('.react-flow');
    if (!element) return;
    
    const background = element.querySelector('.react-flow__background');
    const controls = element.querySelector('.react-flow__controls');
    const attribution = element.querySelector('.react-flow__attribution');
    
    if (background) background.style.display = 'none';
    if (controls) controls.style.display = 'none';
    if (attribution) attribution.style.display = 'none';
    
    try {
      const dataUrl = await toPng(element, {
        backgroundColor: '#ffffff',
        style: {
          transform: 'none',
        }
      });
      
      if (background) background.style.display = '';
      if (controls) controls.style.display = '';
      if (attribution) attribution.style.display = '';
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export to PDF');
        return;
      }
      
      printWindow.document.write(`
        <html>
          <head>
            <title>${idea.title || 'Mind Map'}</title>
            <style>
              body {
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                background-color: #ffffff;
              }
              img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
              }
              @page {
                size: landscape;
                margin: 0;
              }
              @media print {
                body {
                  background-color: #ffffff !important;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <img src="${dataUrl}" onload="setTimeout(function(){ window.print(); window.close(); }, 500);" />
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      if (background) background.style.display = '';
      if (controls) controls.style.display = '';
      if (attribution) attribution.style.display = '';
      console.error('PDF Export failed:', error);
    }
  };

  const nodes = useMemo(() => {
    if (!idea?.nodes) return [];
    const query = searchQuery.toLowerCase().trim();
    
    return idea.nodes.map((n, i) => {
      let isHighlighted = false;
      let isDimmed = false;
      
      if (query) {
        const matches = 
          (n.data?.label || '').toLowerCase().includes(query) || 
          (n.data?.description || '').toLowerCase().includes(query) || 
          (n.data?.details || '').toLowerCase().includes(query);
          
        if (matches) {
          isHighlighted = true;
        } else {
          isDimmed = true;
        }
      }
      
      return {
        ...n,
        type: 'mindMap',
        data: { 
          ...n.data, 
          isRoot: i === 0,
          isHighlighted,
          isDimmed
        },
      };
    });
  }, [idea, searchQuery]);

  const edges = useMemo(() => {
    if (!idea?.edges || !idea?.nodes) return [];
    return idea.edges.map(e => {
      const { sourceHandle, targetHandle, ...clean } = e;
      const sourceNode = idea.nodes.find(n => n.id === e.source);
      const targetNode = idea.nodes.find(n => n.id === e.target);
      const handles = getEdgeHandles(sourceNode, targetNode);
      return {
        ...clean,
        ...handles,
        type: 'simplebezier',
        style: { stroke: isDarkMode ? '#57534e' : '#a8a29e', strokeWidth: 1.5 },
      };
    });
  }, [idea, isDarkMode]);

  const onNodeClick = (event, node) => {
    setSelectedNodeDetails(node.data);
  };

  return (
    <section className="animate-in fade-in slide-in-from-bottom-4 duration-1000 space-y-8 relative">
      {/* Back button */}
      <button
        onClick={() => handleNavigate('bank-idea')}
        className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] opacity-60 hover:opacity-100 transition-all ${theme.text}`}
      >
        <ArrowLeft size={14} /> Bank Idea
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-stone-800' : 'bg-stone-100'}`}>
              <Lightbulb size={18} className={theme.text} />
            </div>
            <h1 className={`text-3xl md:text-4xl font-serif ${theme.text}`}>{idea?.title || 'Untitled'}</h1>
          </div>
          <div className={`flex items-center gap-3 text-xs opacity-50 ${theme.textMuted}`}>
            {idea?.category && (
              <span className="px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-widest" style={{ borderRadius: '9999px', borderColor: isDarkMode ? '#57534e' : '#d6d3d1' }}>
                {idea.category}
              </span>
            )}
            {idea?.date && (
              <span className="flex items-center gap-1.5">
                <Calendar size={13} />
                <span>{formatReadableDate(idea.date)}</span>
              </span>
            )}
          </div>
          {idea?.description && (
            <p className={`text-sm opacity-65 leading-relaxed max-w-2xl ${theme.textMuted}`}>{idea.description}</p>
          )}
        </div>
        
        {/* Actions (Export & Likes) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Like Button */}
          <button 
            onClick={handleLike}
            style={{ borderRadius: '9999px' }}
            className={`flex items-center gap-2 px-4 py-2 border text-[10px] font-bold uppercase tracking-widest transition-all ${
              isLiked 
                ? 'bg-red-500/10 border-red-500/25 text-red-500 hover:bg-red-500/20' 
                : (isDarkMode ? 'border-stone-800 hover:bg-stone-800/40 text-stone-400' : 'border-stone-200 hover:bg-stone-50/80 text-stone-600 hover:shadow-md')
            }`}
          >
            <Heart size={14} className={isLiked ? "fill-red-500 text-red-500" : ""} />
            <span>{likesCount} Likes</span>
          </button>
          
          {/* Export Button */}
          <button 
            onClick={handleExport}
            style={{ borderRadius: '9999px' }}
            className={`flex items-center gap-2 px-4 py-2 border text-[10px] font-bold uppercase tracking-widest transition-all ${
              isDarkMode ? 'border-stone-800 hover:bg-stone-800/40 text-stone-400' : 'border-stone-200 hover:bg-stone-50/80 text-stone-600 hover:shadow-md'
            }`}
          >
            <Download size={14} />
            <span>Export Image</span>
          </button>

          {/* Export PDF Button */}
          <button 
            onClick={handleExportPDF}
            style={{ borderRadius: '9999px' }}
            className={`flex items-center gap-2 px-4 py-2 border text-[10px] font-bold uppercase tracking-widest transition-all ${
              isDarkMode ? 'border-stone-800 hover:bg-stone-800/40 text-stone-400' : 'border-stone-200 hover:bg-stone-50/80 text-stone-600 hover:shadow-md'
            }`}
          >
            <FileText size={14} />
            <span>Export PDF</span>
          </button>
        </div>
      </div>

      {/* Mind Map Canvas */}
      {nodes.length > 0 ? (
        <div style={{ height: '65vh' }} className={`relative border ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
          {/* Canvas Search overlay */}
          <div className="absolute top-4 left-4 z-10 flex items-center gap-2 max-w-xs w-full">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-md w-full ${isDarkMode ? 'bg-stone-950/80 border-stone-800' : 'bg-white/80 border-stone-200'}`}>
              <Search size={12} className="opacity-40" />
              <input 
                type="text" 
                placeholder="Cari nod..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full bg-transparent text-[11px] outline-none ${theme.text} placeholder:opacity-30`}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="opacity-40 hover:opacity-100"><X size={10} /></button>
              )}
            </div>
          </div>

          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onInit={(instance) => { rfInstance.current = instance; }}
              fitView
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={true}
              onNodeClick={onNodeClick}
              attributionPosition="bottom-left"
            >
              <Controls className={isDarkMode ? 'bg-stone-800 border-stone-700' : ''} />
              <Background color={isDarkMode ? '#292524' : '#e7e5e4'} gap={20} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
      ) : (
        <div className="text-center py-20 opacity-40">
          <p className="text-[10px] font-bold uppercase tracking-widest">Empty mind map</p>
        </div>
      )}

      {/* Side Details Drawer */}
      {selectedNodeDetails && (
        <div className="fixed inset-0 z-[110] flex justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setSelectedNodeDetails(null)}
          />
          {/* Drawer content */}
          <div className={`relative w-full max-w-md h-full shadow-2xl p-8 flex flex-col gap-6 transform transition-transform duration-300 translate-x-0 ${isDarkMode ? 'bg-stone-900 border-l border-stone-800 text-stone-100' : 'bg-white border-l border-stone-100 text-stone-900'}`}>
            <button 
              onClick={() => setSelectedNodeDetails(null)}
              className="absolute top-6 right-6 p-2 rounded-full hover:opacity-60 transition-opacity"
            >
              <X size={18} />
            </button>
            
            <div className="mt-8 space-y-4 flex-1 overflow-y-auto no-scrollbar">
              <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest opacity-45">
                <span>Node Insight</span>
              </div>
              <h2 className="text-2xl font-serif font-semibold leading-snug">{selectedNodeDetails.label || 'Untitled Node'}</h2>
              {selectedNodeDetails.description && (
                <p className="text-xs italic opacity-60 leading-relaxed">{selectedNodeDetails.description}</p>
              )}
              <div className={`w-full h-px ${isDarkMode ? 'bg-stone-850' : 'bg-stone-100'}`} />
              <div className="text-sm leading-relaxed whitespace-pre-wrap opacity-80 font-serif">
                {selectedNodeDetails.details || 'Tiada maklumat lanjut untuk nod ini.'}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BankIdeaView;
